import React, { useEffect, useState } from 'react';
import { Plus, Trash2, FileDown, MessageCircle, Search, TrendingDown, TrendingUp, Wallet, Copy, Check } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';

export default function Laundry() {
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState({ entries: [], totalCharged: 0, totalPaid: 0, balance: 0 });
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('month');
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [chargeForm, setChargeForm] = useState({ amount: '', date: today(), description: '' });
  const [paymentForm, setPaymentForm] = useState({ amount: '', date: today(), notes: '' });
  const [defaultAmount, setDefaultAmount] = useState('');

  function today() { return new Date().toISOString().split('T')[0]; }

  useEffect(() => {
    api.get('/students', { params: { status: 'ACTIVE' } }).then(r => setStudents(r.data));
    api.get('/settings').then(r => setDefaultAmount(r.data.laundryDefaultAmount || '')).catch(() => {});
  }, []);

  const loadLedger = (studentId, p = period) => {
    if (!studentId) return;
    api.get(`/laundry/student/${studentId}`, { params: { period: p } })
      .then(r => setLedger(r.data))
      .catch(console.error);
  };

  const selectStudent = s => {
    setSelected(s);
    setShareUrl('');
    loadLedger(s.id);
  };

  const addCharge = async e => {
    e.preventDefault();
    try {
      await api.post('/laundry', { ...chargeForm, studentId: selected.id });
      toast.success('Charge added');
      setShowChargeForm(false);
      setChargeForm({ amount: '', date: today(), description: '' });
      loadLedger(selected.id);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const addPayment = async e => {
    e.preventDefault();
    try {
      await api.post('/laundry/payment', { ...paymentForm, studentId: selected.id });
      toast.success('Payment recorded');
      setShowPaymentForm(false);
      setPaymentForm({ amount: '', date: today(), notes: '' });
      loadLedger(selected.id);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const doDelete = async () => {
    try {
      const { id, type } = confirmDelete;
      if (type === 'CHARGE') await api.delete(`/laundry/record/${id}`);
      else await api.delete(`/laundry/payment/${id}`);
      toast.success('Deleted');
      setConfirmDelete(null);
      loadLedger(selected.id);
    } catch { toast.error('Error'); setConfirmDelete(null); }
  };

  const downloadPDF = async () => {
    try {
      const res = await api.get(`/laundry/pdf/${selected.id}`, { params: { period }, responseType: 'blob' });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = href;
      a.download = `laundry-${selected.registrationNo}-${period}.pdf`;
      a.click();
      URL.revokeObjectURL(href);
    } catch { toast.error('PDF download failed'); }
  };

  const getShareLink = async () => {
    try {
      const { data } = await api.post('/laundry/share-link', { studentId: selected.id });
      setShareUrl(data.url);
    } catch { toast.error('Error generating link'); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    const phone = selected.phone.replace(/\D/g, '');
    const msg = encodeURIComponent(`Hi ${selected.firstName}, here is your laundry statement: ${shareUrl}`);
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  const filtered = students.filter(s =>
    `${s.firstName} ${s.lastName} ${s.registrationNo} ${s.phone}`.toLowerCase().includes(search.toLowerCase())
  );

  const balanceColor = ledger.balance > 0 ? 'text-red-600' : 'text-green-600';
  const balanceLabel = ledger.balance > 0 ? 'Outstanding' : ledger.balance < 0 ? 'Credit' : 'Settled';

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-xl font-bold text-gray-800 mb-4">Laundry</h2>

      {!selected ? (
        <div>
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search student..."
              className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div className="space-y-2">
            {filtered.map(s => (
              <button key={s.id} onClick={() => selectStudent(s)}
                className="w-full bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-left flex items-center justify-between hover:border-brand-300 transition-colors">
                <div>
                  <p className="font-semibold text-gray-800">{s.firstName} {s.lastName}</p>
                  <p className="text-xs text-gray-400">{s.registrationNo} · {s.phone}</p>
                </div>
                <p className="text-xs text-gray-400">{s.tenancies?.[0]?.room?.roomNumber || '-'}</p>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div>
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => { setSelected(null); setLedger({ entries: [], totalCharged: 0, totalPaid: 0, balance: 0 }); }} className="text-brand-600 text-sm">← Back</button>
            <div className="flex-1">
              <p className="font-bold text-gray-800">{selected.firstName} {selected.lastName}</p>
              <p className="text-xs text-gray-400">{selected.registrationNo}</p>
            </div>
          </div>

          {/* Period filter */}
          <div className="flex gap-2 mb-4">
            {['week', 'month', 'all'].map(p => (
              <button key={p} onClick={() => { setPeriod(p); loadLedger(selected.id, p); }}
                className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${period === p ? 'bg-brand-700 text-white' : 'bg-white text-gray-600 border'}`}>
                {p === 'all' ? 'All Time' : `This ${p.charAt(0).toUpperCase() + p.slice(1)}`}
              </button>
            ))}
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-red-50 rounded-xl p-3 text-center">
              <TrendingDown size={16} className="text-red-400 mx-auto mb-1" />
              <p className="text-xs text-red-500">Charged</p>
              <p className="text-sm font-bold text-red-700">Rs. {ledger.totalCharged.toLocaleString()}</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <TrendingUp size={16} className="text-green-400 mx-auto mb-1" />
              <p className="text-xs text-green-500">Paid</p>
              <p className="text-sm font-bold text-green-700">Rs. {ledger.totalPaid.toLocaleString()}</p>
            </div>
            <div className={`rounded-xl p-3 text-center ${ledger.balance > 0 ? 'bg-orange-50' : 'bg-blue-50'}`}>
              <Wallet size={16} className={`${ledger.balance > 0 ? 'text-orange-400' : 'text-blue-400'} mx-auto mb-1`} />
              <p className={`text-xs ${ledger.balance > 0 ? 'text-orange-500' : 'text-blue-500'}`}>{balanceLabel}</p>
              <p className={`text-sm font-bold ${balanceColor}`}>Rs. {Math.abs(ledger.balance).toLocaleString()}</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button onClick={() => { setShowChargeForm(true); setShowPaymentForm(false); setChargeForm(p => ({ ...p, amount: defaultAmount })); }}
              className="bg-red-50 hover:bg-red-100 text-red-700 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2">
              <Plus size={16} /> Add Charge
            </button>
            <button onClick={() => { setShowPaymentForm(true); setShowChargeForm(false); }}
              className="bg-green-50 hover:bg-green-100 text-green-700 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2">
              <Plus size={16} /> Add Payment
            </button>
          </div>

          {/* Charge form */}
          {showChargeForm && (
            <form onSubmit={addCharge} className="bg-white rounded-xl p-4 border border-red-100 shadow-sm mb-4 space-y-3">
              <p className="text-sm font-semibold text-red-700">New Laundry Charge</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600">Amount (Rs.) *</label>
                  <input type="number" required value={chargeForm.amount} onChange={e => setChargeForm(p => ({ ...p, amount: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Date *</label>
                  <input type="date" required value={chargeForm.date} onChange={e => setChargeForm(p => ({ ...p, date: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Description</label>
                <input value={chargeForm.description} onChange={e => setChargeForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="e.g. 2 sets of clothes"
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowChargeForm(false)} className="flex-1 border border-gray-300 text-gray-600 py-2 rounded-lg text-sm">Cancel</button>
                <button type="submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg text-sm">Add Charge</button>
              </div>
            </form>
          )}

          {/* Payment form */}
          {showPaymentForm && (
            <form onSubmit={addPayment} className="bg-white rounded-xl p-4 border border-green-100 shadow-sm mb-4 space-y-3">
              <p className="text-sm font-semibold text-green-700">Record Payment</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600">Amount (Rs.) *</label>
                  <input type="number" required value={paymentForm.amount} onChange={e => setPaymentForm(p => ({ ...p, amount: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Date *</label>
                  <input type="date" required value={paymentForm.date} onChange={e => setPaymentForm(p => ({ ...p, date: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Notes</label>
                <input value={paymentForm.notes} onChange={e => setPaymentForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="e.g. Cash payment, advance for next month"
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowPaymentForm(false)} className="flex-1 border border-gray-300 text-gray-600 py-2 rounded-lg text-sm">Cancel</button>
                <button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg text-sm">Record Payment</button>
              </div>
            </form>
          )}

          {/* Share & PDF */}
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-4">
            <p className="text-xs font-semibold text-gray-600 mb-3">Send Statement</p>
            <div className="flex gap-2 mb-3">
              <button onClick={downloadPDF} className="flex items-center gap-1.5 text-xs bg-brand-50 text-brand-700 px-3 py-2 rounded-lg hover:bg-brand-100 flex-1 justify-center">
                <FileDown size={14} /> Download PDF
              </button>
              <button onClick={getShareLink} className="flex items-center gap-1.5 text-xs bg-blue-50 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-100 flex-1 justify-center">
                <MessageCircle size={14} /> Get Share Link
              </button>
            </div>
            {shareUrl && (
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <p className="text-xs text-gray-500 break-all">{shareUrl}</p>
                <div className="flex gap-2">
                  <button onClick={copyLink} className="flex items-center gap-1 text-xs bg-white border text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-50 flex-1 justify-center">
                    {copied ? <><Check size={12} className="text-green-600" /> Copied</> : <><Copy size={12} /> Copy Link</>}
                  </button>
                  <button onClick={shareWhatsApp} className="flex items-center gap-1 text-xs bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-600 flex-1 justify-center">
                    <MessageCircle size={12} /> WhatsApp
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Ledger */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-700">Ledger</p>
              <p className="text-xs text-gray-400">{ledger.entries.length} entries</p>
            </div>
            <div className="divide-y divide-gray-50">
              {ledger.entries.map(e => (
                <div key={e.id} className="flex items-center px-4 py-3 gap-3">
                  <div className={`w-1.5 h-8 rounded-full flex-shrink-0 ${e.type === 'CHARGE' ? 'bg-red-400' : 'bg-green-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{e.description || (e.type === 'PAYMENT' ? 'Payment received' : 'Laundry charge')}</p>
                    <p className="text-xs text-gray-400">{new Date(e.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-bold ${e.type === 'CHARGE' ? 'text-red-600' : 'text-green-600'}`}>
                      {e.type === 'CHARGE' ? '-' : '+'}Rs. {e.amount.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400">{e.type === 'CHARGE' ? 'Charge' : 'Payment'}</p>
                  </div>
                  <button onClick={() => setConfirmDelete({ id: e.raw?.id ?? e.id.replace(/^[cp]-/, ''), type: e.type })}
                    className="text-gray-300 hover:text-red-500 p-1 flex-shrink-0">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {ledger.entries.length === 0 && (
                <p className="text-center text-gray-400 py-10 text-sm">No records for this period</p>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title={`Delete ${confirmDelete?.type === 'CHARGE' ? 'Charge' : 'Payment'}`}
        message="Are you sure you want to delete this record? This cannot be undone."
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
